import React, { useCallback, useRef, useEffect, useMemo } from 'react'
import { List, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'

import ThemeInterface from '@/theme-interface'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import {
  selectConversations,
  selectCurrentConversationId,
  selectHasMoreConversations,
  selectUnreadConversationIds,
  selectConversation,
  type Conversation,
  markConversationAsRead,
  getConversations,
  deleteConversation,
} from '@/redux/ducks/ai/chat'
import lang from '@/lang'
import AiChatConversationListItem from './ai-chat-conversation-list-item'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  scrollContainer: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
  },
  list: {
    padding: 0,
  },
  emptyState: {
    padding: 20,
    textAlign: 'center',
    color: theme.textMuted,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingIndicator: {
    padding: 12,
    textAlign: 'center',
    color: theme.textMuted,
  },
}))

interface AiChatConversationListProps {
  onConversationSelect?: () => void
}

export default function AiChatConversationList(props: AiChatConversationListProps) {
  const { onConversationSelect } = props
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)

  const conversations = useAppSelector(selectConversations)
  const currentConversationId = useAppSelector(selectCurrentConversationId)
  const hasMoreConversations = useAppSelector(selectHasMoreConversations)
  const unreadConversationIds = useAppSelector(selectUnreadConversationIds)

  useEffect(() => {
    if (conversations.length === 0) {
      dispatch(getConversations({ limit: 10, offset: 0 }))
    }
  }, [])

  const handleConversationClick = (conversationId: string) => {
    dispatch(selectConversation(conversationId))
    dispatch(markConversationAsRead(conversationId))
    if (onConversationSelect) {
      onConversationSelect()
    }
  }

  const handleDeleteConversation = (conversationId: string) => {
    dispatch(deleteConversation({ conversationId }))
  }

  const handleLoadMore = useCallback(() => {
    if (hasMoreConversations) {
      dispatch(getConversations({ limit: 10, offset: conversations.length }))
    }
  }, [hasMoreConversations, conversations.length, dispatch])

  useEffect(() => {
    if (!loadMoreTriggerRef.current) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasMoreConversations) {
          handleLoadMore()
        }
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.1,
      },
    )

    observerRef.current.observe(loadMoreTriggerRef.current)

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [hasMoreConversations, handleLoadMore])

  const conversationElements = useMemo(() => {
    return conversations.map((conversation: Conversation) => (
      <AiChatConversationListItem
        key={conversation.id}
        conversation={conversation}
        isSelected={conversation.id === currentConversationId}
        isUnread={unreadConversationIds.includes(conversation.id)}
        onSelect={handleConversationClick}
        onDelete={handleDeleteConversation}
      />
    ))
  }, [conversations, currentConversationId, unreadConversationIds, handleConversationClick])

  return (
    <div className={classes.container}>
      <div ref={scrollContainerRef} className={classes.scrollContainer}>
        {conversations.length === 0
          ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                {lang.ai.chat.conversationList.empty}
              </Typography>
            </div>
          )
          : (
            <List className={classes.list}>
              {conversationElements}
              {hasMoreConversations && (
                <div ref={loadMoreTriggerRef} className={classes.loadingIndicator}>
                  <Typography variant="caption">
                    {lang.ai.chat.conversationList.loading}
                  </Typography>
                </div>
              )}
            </List>
          )}
      </div>
    </div>
  )
}
